import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

type Step = 1 | 2 | 3;

interface TestResult {
  valid: boolean;
  orgName?: string;
  instanceUrl?: string;
  error?: string;
}

export default function DynamicsCreate() {
  const navigate = useNavigate();
  const api = useApi();

  const [step, setStep] = useState<Step>(1);
  const [displayName, setDisplayName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [instanceUrl, setInstanceUrl] = useState("");
  const [secretExpiresAt, setSecretExpiresAt] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [environments, setEnvironments] = useState<{ displayName: string; instanceUrl: string }[]>([]);
  const [discovering, setDiscovering] = useState(false);

  async function handleDiscoverEnvironments() {
    if (!tenantId.trim() || !clientId.trim() || !clientSecret.trim()) {
      setError("Enter your Tenant ID, Client ID, and Client Secret first, then click Discover.");
      return;
    }
    setDiscovering(true);
    setError(null);
    try {
      const resp = await api.admin.post("Dynamics/DiscoverEnvironments", {
        tenantId: tenantId.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim()
      });
      const data = await resp.json() as { environments: { displayName: string; instanceUrl: string }[] };
      if (Array.isArray(data?.environments) && data.environments.length > 0) {
        setEnvironments(data.environments);
        if (!instanceUrl) setInstanceUrl(data.environments[0].instanceUrl);
      } else {
        setError("No Dynamics 365 environments found. You may not have Power Platform admin access — enter the URL manually instead.");
      }
    } catch {
      setError("Could not discover environments. Enter the Instance URL manually.");
    } finally {
      setDiscovering(false);
    }
  }

  async function handleTestConnection() {
    if (!tenantId.trim()) { setError("Enter your Azure / Entra Tenant ID."); return; }
    if (!clientId.trim()) { setError("Enter your Application (Client) ID."); return; }
    if (!clientSecret.trim()) { setError("Enter your Client Secret."); return; }
    if (!instanceUrl.trim()) { setError("Enter your Dynamics 365 Instance URL."); return; }
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const resp = await api.admin.post("Dynamics/Configs/Test", {
        tenantId: tenantId.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        instanceUrl: instanceUrl.trim()
      });
      const data = await resp.json() as TestResult;
      setTestResult(data);
      if (data.valid) {
        if (!displayName) setDisplayName(data.orgName || "Dynamics 365");
        setStep(2);
      } else {
        setError(data.error || "Connection test failed. Check your credentials.");
      }
    } catch {
      setError("Failed to test connection. Please try again.");
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const resp = await api.admin.post("Dynamics/Configs", {
        displayName: displayName.trim() || testResult?.orgName || "Dynamics 365",
        tenantId: tenantId.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        instanceUrl: instanceUrl.trim(),
        clientSecretExpiresAt: secretExpiresAt ? new Date(secretExpiresAt).toISOString() : null
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body?.error ?? "Failed to save. Please try again.");
        return;
      }
      setStep(3);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  const steps = [
    { n: 1, label: "Connect" },
    { n: 2, label: "Settings" },
    { n: 3, label: "Done" }
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? "bg-cornflower-blue-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > s.n ? "✓" : s.n}
              </div>
              <span className={`text-sm ${step === s.n ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-3" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 1: Credentials ── */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Connect your Dynamics 365 org</h2>
          <p className="text-sm text-slate-500 mb-6">
            Register a Microsoft Entra app and grant it access to Dynamics 365, then paste the credentials below.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800 space-y-2">
            <p className="font-medium">How to get your Dynamics 365 app credentials:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Go to <strong>Azure Portal → Microsoft Entra ID → App registrations</strong></li>
              <li>Click <strong>New registration</strong> — name it "Velaro Integration", single tenant</li>
              <li>Copy the <strong>Application (client) ID</strong> and <strong>Directory (tenant) ID</strong></li>
              <li>Under <strong>Certificates &amp; secrets</strong>, create a new Client Secret — copy the value immediately</li>
              <li>In <strong>Dynamics 365 Admin Center → Settings → Users</strong>, create a new <strong>Application User</strong> and paste the Client ID from step 3</li>
              <li>Assign the Application User a security role (e.g. <code className="bg-blue-100 px-1 rounded">Sales Person</code> or custom role with read/write on Contacts, Leads, Accounts, Annotations)</li>
            </ol>
            <a href="https://learn.microsoft.com/en-us/power-apps/developer/data-platform/authenticate-oauth" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">View Microsoft OAuth docs →</a>
          </div>

          <div className="space-y-4 mb-4">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">Tenant ID</span>
              <input type="text" className="input font-mono text-sm" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={tenantId} onChange={(e) => setTenantId(e.target.value)} autoFocus />
              <span className="text-xs text-slate-400">Found in Azure Portal → Microsoft Entra ID → Overview</span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">Application (Client) ID</span>
              <input type="text" className="input font-mono text-sm" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={clientId} onChange={(e) => setClientId(e.target.value)} />
              <span className="text-xs text-slate-400">The App Registration client ID from step 3</span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">Client Secret</span>
              <input type="password" className="input font-mono text-sm" placeholder="••••••••••••••••••••••••••••••••"
                value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
              <span className="text-xs text-slate-400">Copy from Certificates &amp; secrets — only shown once</span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">Secret Expiry Date <span className="text-slate-400 font-normal">(optional)</span></span>
              <input type="date" className="input text-sm w-48"
                value={secretExpiresAt} onChange={(e) => setSecretExpiresAt(e.target.value)} />
              <span className="text-xs text-slate-400">Set this to get a warning email 30 days before the secret expires. Found in Azure → Certificates &amp; secrets → Expires column.</span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">Instance URL</span>
              {environments.length > 0 ? (
                <select className="input text-sm" value={instanceUrl} onChange={(e) => setInstanceUrl(e.target.value)}>
                  {environments.map((env) => (
                    <option key={env.instanceUrl} value={env.instanceUrl}>
                      {env.displayName} — {env.instanceUrl}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex gap-2">
                  <input type="text" className="input font-mono text-sm flex-1" placeholder="https://yourorg.crm.dynamics.com"
                    value={instanceUrl} onChange={(e) => setInstanceUrl(e.target.value)} />
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md border border-slate-300 whitespace-nowrap"
                    onClick={handleDiscoverEnvironments}
                    disabled={discovering || !tenantId.trim() || !clientId.trim() || !clientSecret.trim()}
                  >
                    {discovering ? "Searching..." : "Auto-Discover"}
                  </button>
                </div>
              )}
              <span className="text-xs text-slate-400">
                {environments.length > 0
                  ? "Select your Dynamics 365 environment from the list above."
                  : "Enter your D365 org URL, or click Auto-Discover after filling in the credentials above."}
              </span>
              {environments.length > 0 && (
                <button type="button" className="text-xs text-blue-500 hover:underline self-start"
                  onClick={() => { setEnvironments([]); setInstanceUrl(""); }}>
                  Enter URL manually instead
                </button>
              )}
            </label>
          </div>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-end gap-2">
            <SecondaryBlueButton label="Cancel" onClick={() => navigate("/integrations")} />
            {testing ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Testing connection…</div>
            ) : (
              <PrimaryBlueButton label="Test & Continue" onClick={handleTestConnection} />
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: Settings ── */}
      {step === 2 && testResult?.valid && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-lg">✓</span>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Connected to Dynamics 365</div>
              <div className="text-sm text-slate-500">
                Org: {testResult.orgName}
                {testResult.instanceUrl && <span className="ml-2 text-slate-400 text-xs">{testResult.instanceUrl}</span>}
              </div>
            </div>
          </div>

          <label className="flex flex-col gap-1 mb-6">
            <span className="text-sm font-medium">Display Name</span>
            <input className="input" placeholder="e.g. Production Dynamics"
              value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            <span className="text-xs text-slate-400">How this connection appears in Velaro.</span>
          </label>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-between">
            <SecondaryBlueButton label="Back" onClick={() => setStep(1)} />
            {saving ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Saving…</div>
            ) : (
              <PrimaryBlueButton label="Save & Finish" onClick={handleSave} />
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Done ── */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-500 text-3xl">✓</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Dynamics 365 connected!</h2>
          <p className="text-sm text-slate-500 mb-6">
            Your Dynamics 365 org is now linked to Velaro. AI workflow nodes can query contacts, create leads, and push notes automatically.
          </p>
          <div className="flex justify-center gap-3">
            <SecondaryBlueButton label="Back to Integrations" onClick={() => navigate("/integrations")} />
            <PrimaryBlueButton label="Manage Dynamics 365" onClick={() => navigate("/integrations/dynamics")} />
          </div>
        </div>
      )}
    </div>
  );
}
